import { isWebViewOnMobile } from './user-agent';
import { getUser } from 'app/stores/user-store';
import { getCompany } from 'stores/company-store';
import { EncryptUserHelpers } from '@workivate/tho-web-shared';

const MMHN_TYPES = ['MMHN', 'Manulife Mental Health Network'];
const SECRET_KEY = 'manulife_mhcs_icbt_integration';
const MANULIFE_SUFFIXES = ['-emb', '-end'];

/*
  We enable Manulife features for the following originators:

    - Manulife (the intended target of this feature in production)
    - Telus Test Originator (allowlisted originator in test for QA)
    - Unum LifeWorks (allowlisted originator in production for QA)

    Anywhere we code a feature for Manulife, we should use this check so that our QA team can test the feature on Test and in Production

    If there is no originator, we return undefined so that the calling function can decide what to do
*/
const MANULIFE_ORIGINATORS = [
  'manulife',
  'telus test originator',
  'unum lifeworks',
];

export const isManulifeFeatureEligible = () => {
  const originator = getUser().originator || '';

  if (!originator) {
    return undefined;
  }

  return MANULIFE_ORIGINATORS.includes(originator.toLowerCase());
};

// TODO replace this with isManulifeFeatureEligible() either when we need to test a specific feature or when we have a bit more time for testing to make sure we don't break anything.  This is a legacy specific check for the Manulife originator only.
export const isManulifeOriginator = () => {
  const originator = getUser().originator || '';

  // If originator is not set, we can't determine if it's Manulife
  if (!originator) {
    return undefined;
  }

  return originator.toLowerCase() === 'manulife';
};

// Currently reads only external_ids.manulife.
// TODO update this if allowlisted test originators receive different external ID keys.
export const getManulifeExternalId = () => {
  const user = getUser();
  const externalId = user.external_ids?.manulife ?? '';

  return externalId;
};

// This function only reads Manulife originator but 'isManulife' defaults to true so may not need updating.
export const isManulifeWebView = () => {
  const isManulifeOrig = isManulifeOriginator();

  // If isManulifeOrig is undefined, we return a default value until we can determine if it's Manulife
  const isManulife = isManulifeOrig === undefined ? true : isManulifeOrig;

  return isWebViewOnMobile() && isManulife;
};

// This function only allows Manulife originator, not the other allowlisted originators
// TODO update this to use the full list of originators to enable testing of Manulife features
export const isManulifeMMHNCompany = () => {
  const companyType = getCompany()['company_type'] || '';

  return isManulifeOriginator() && MMHN_TYPES.includes(companyType);
};

export const isManulifeEmbOrEnd = () => {
  const companyName = getCompany()['name'] || '';
  const lowerCompanyName = companyName.toLowerCase();

  return (
    isManulifeFeatureEligible() &&
    MANULIFE_SUFFIXES.some(suffix => lowerCompanyName.endsWith(suffix))
  );
};

export const getManulifeUrlWithParam = (url: string) => {
  const MONITORED_URLS = ['https://manulife.myicbt.com/'];

  const externalId = getManulifeExternalId();

  if (!externalId || !MONITORED_URLS.includes(url)) {
    return url;
  }

  const encryptedExternalId = EncryptUserHelpers.encryptUserId(
    externalId,
    SECRET_KEY,
  );

  const newUrl = new URL(url);
  newUrl.searchParams.set('param', encryptedExternalId ?? '');

  return newUrl.toString();
};
